import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import api, { formatDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import { MapPin, Truck, Plane, PackageSearch, CircleDot, RefreshCw, CheckCircle2, Users, Phone, Mail, LogIn, MessageCircle, Navigation } from "lucide-react";
import { LiveTrackMap } from "../components/LiveTrackMap";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function statusColor(status) {
  const s = String(status || "").toLowerCase();
  if (s.includes("deliver")) return "text-[#2F6F4E] bg-[#E7F2EC]";
  if (s.includes("cancel")) return "text-[#8A2526] bg-[#F7E7E7]";
  return "text-[#9A6A1E] bg-[#FBF1DE]";
}

export default function PublicTrack() {
  const { token } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [contacts, setContacts] = useState(null);
  const [err, setErr] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [eta, setEta] = useState(null);

  const fetchData = useCallback(() => {
    axios.get(`${API}/p/track/${token}`)
      .then((r) => { setData(r.data); setUpdatedAt(new Date()); setErr(""); })
      .catch((e) => setErr(e.response?.data?.detail || "Tracking link is not valid"));
  }, [token]);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 15000); // live refresh every 15s
    return () => clearInterval(id);
  }, [fetchData]);

  useEffect(() => {
    let active = true;
    const fetchEta = () => axios.get(`${API}/p/track/${token}/eta`).then((r) => { if (active) setEta(r.data); }).catch(() => {});
    fetchEta();
    const id = setInterval(fetchEta, 20000);
    return () => { active = false; clearInterval(id); };
  }, [token]);

  useEffect(() => {
    if (user && token) {
      api.get(`/track/${token}/contacts`).then((r) => setContacts(r.data.contacts || [])).catch(() => setContacts([]));
    }
  }, [user, token]);

  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8] text-[#8A2526] text-sm p-6" data-testid="public-track-error">{err}</div>
  );
  if (!data) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8] text-[#68665E] text-sm">Loading tracking…</div>
  );

  const isAir = data.transport_mode === "Air";

  return (
    <div className="min-h-screen bg-[#FDFCF8]" data-testid="public-track-page">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <img src="/ito-logo.png" alt="ITO" className="w-10 h-10 object-contain" />
          <div>
            <div className="font-headings font-bold text-[#1A1A18] text-sm leading-tight">INDIAN TRANSPORT ORGANISATION</div>
            <div className="text-[10px] uppercase tracking-widest text-[#68665E]">Live Consignment Tracking</div>
          </div>
        </div>

        <div className="bg-white border border-[#E5E3DB] p-6 mb-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-wide text-[#68665E]">Lorry Receipt</div>
              <div className="font-headings text-3xl font-bold text-[#1A1A18]" data-testid="track-lr-number">{data.lr_number}</div>
              <div className="text-sm text-[#68665E] mt-1">{formatDate(data.lr_date)}</div>
            </div>
            <span className={`text-xs font-medium px-3 py-1 rounded-full ${statusColor(data.status)}`} data-testid="track-status">{String(data.status || "").replace(/_/g, " ")}</span>
          </div>

          <div className="flex items-center gap-3 mt-5 text-sm">
            <div className="flex-1">
              <div className="text-[11px] uppercase text-[#68665E]">From</div>
              {data.from_label && <div className="text-[11px] font-semibold text-[#2B5FB3]" data-testid="track-from-label">{data.from_label}</div>}
              <div className="font-medium" data-testid="track-from">{data.from_location || "—"}</div>
            </div>
            <div className="text-[#C84B31]">{isAir ? <Plane className="w-5 h-5" /> : <Truck className="w-5 h-5" />} →</div>
            <div className="flex-1 text-right">
              <div className="text-[11px] uppercase text-[#68665E]">To</div>
              {data.to_label && <div className="text-[11px] font-semibold text-[#2B5FB3]" data-testid="track-to-label">{data.to_label}</div>}
              <div className="font-medium" data-testid="track-to">{data.to_location || "—"}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-5 text-sm">
            {data.goods_description && <Info label="Goods" value={data.goods_description} />}
            {data.no_of_packages ? <Info label="Packages" value={data.no_of_packages} /> : null}
            {data.weight_kg ? <Info label="Weight" value={`${data.weight_kg} kg`} /> : null}
            {data.vehicle_number && <Info label="Vehicle" value={data.vehicle_number} mono />}
            {isAir && data.flight_number && <Info label="Flight" value={data.flight_number} mono />}
            {isAir && data.awb_number && <Info label="AWB" value={data.awb_number} mono />}
          </div>

          {isAir && (data.flight_number || data.awb_number) && (
            <div className="flex flex-wrap gap-2 mt-4">
              {data.flight_number && (
                <a className="inline-flex items-center gap-1.5 text-xs border border-[#E5E3DB] px-3 py-2 hover:bg-[#FAFAF9]" href={`https://www.flightaware.com/live/flight/${encodeURIComponent(data.flight_number.replace(/\s/g, ""))}`} target="_blank" rel="noreferrer" data-testid="track-flight-link"><Plane className="w-3 h-3" /> Live Flight Tracking</a>
              )}
              {data.awb_number && (
                <a className="inline-flex items-center gap-1.5 text-xs border border-[#E5E3DB] px-3 py-2 hover:bg-[#FAFAF9]" href={`https://www.google.com/search?q=${encodeURIComponent(data.awb_number + " air waybill AWB tracking")}`} target="_blank" rel="noreferrer" data-testid="track-awb-link"><PackageSearch className="w-3 h-3" /> Live AWB Tracking</a>
              )}
            </div>
          )}

          {data.delivery_maps_url && (
            <a className="inline-flex items-center gap-1.5 text-xs text-[#C84B31] hover:underline mt-3" href={data.delivery_maps_url} target="_blank" rel="noreferrer"><MapPin className="w-3 h-3" /> Delivery location on map</a>
          )}
        </div>

        {eta?.available && (
          <div className="bg-[#EAF0FB] border border-[#C5D6F2] p-4 mb-4 flex items-center gap-3" data-testid="track-eta">
            <div className="w-10 h-10 rounded-full bg-[#2B5FB3] flex items-center justify-center shrink-0">
              <Navigation className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wide text-[#2B5FB3] font-semibold">Live ETA · {eta.driver_label} en route</div>
              <div className="text-lg font-bold text-[#1A1A18]" data-testid="track-eta-time">{eta.duration_text || "—"} away · {eta.distance_text || ""}</div>
              <div className="text-xs text-[#68665E]">Est. arrival ~ {eta.eta_iso ? new Date(eta.eta_iso).toLocaleTimeString() : "—"}{eta.destination ? ` · ${eta.destination}` : ""}</div>
            </div>
          </div>
        )}

        {!isAir && <LiveTrackMap token={token} />}

        <div className="bg-white border border-[#E5E3DB] p-6">
          <div className="text-xs uppercase tracking-wide text-[#68665E] mb-4">Status Timeline</div>
          {(data.timeline || []).length === 0 ? (
            <div className="text-sm text-[#A09E96]">No updates yet.</div>
          ) : (
            <ol className="relative border-l border-[#E5E3DB] ml-2">
              {data.timeline.map((t, i) => {
                const last = i === data.timeline.length - 1;
                const delivered = String(t.text).toLowerCase().includes("deliver");
                return (
                  <li key={`${t.time || t.text || "evt"}-${i}`} className="ml-5 pb-5 last:pb-0" data-testid={`track-event-${i}`}>
                    <span className="absolute -left-[7px] flex items-center justify-center">
                      {delivered ? <CheckCircle2 className="w-3.5 h-3.5 text-[#2F6F4E] bg-white" /> : <CircleDot className={`w-3.5 h-3.5 bg-white ${last ? "text-[#C84B31]" : "text-[#A09E96]"}`} />}
                    </span>
                    <div className="text-sm text-[#1A1A18]">{t.text}</div>
                    <div className="text-[11px] text-[#68665E]">{formatTime(t.time)}</div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        {user ? (
          contacts && contacts.length > 0 && (
            <div className="bg-white border border-[#E5E3DB] p-6 mt-4" data-testid="track-contacts">
              <div className="text-xs uppercase tracking-wide text-[#68665E] mb-4 flex items-center gap-2"><Users className="w-4 h-4" /> People Involved</div>
              <div className="divide-y divide-[#F0EDE5]">
                {contacts.map((c, i) => {
                  const wa = String(c.phone || "").replace(/[^0-9]/g, "");
                  const waNum = wa.length === 10 ? "91" + wa : wa;
                  return (
                    <div key={`${c.group}-${c.name}-${i}`} className="py-2.5 flex items-center justify-between gap-3" data-testid={`track-contact-${i}`}>
                      <div className="min-w-0">
                        <div className="text-[11px] uppercase text-[#68665E]">{c.group}</div>
                        <div className="font-medium text-[#1A1A18] truncate">{c.name || "—"}{c.role ? ` · ${c.role}` : ""}</div>
                        {c.email && <a href={`mailto:${c.email}`} className="text-xs text-[#68665E] hover:text-[#C84B31] inline-flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</a>}
                      </div>
                      {c.phone && (
                        <div className="flex items-center gap-2 shrink-0">
                          <a href={`tel:${c.phone}`} className="btn-secondary text-xs inline-flex items-center gap-1" data-testid={`track-call-${i}`}><Phone className="w-3 h-3" />{c.phone}</a>
                          <a href={`https://wa.me/${waNum}`} target="_blank" rel="noreferrer" className="text-[#1f8a4c]" title="WhatsApp" data-testid={`track-wa-${i}`}><MessageCircle className="w-4 h-4" /></a>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )
        ) : user === false ? (
          <div className="bg-white border border-[#E5E3DB] p-6 mt-4 text-center" data-testid="track-login-prompt">
            <div className="text-sm text-[#68665E] mb-3">Log in to view contact details of everyone involved in this consignment.</div>
            <Link to="/login" className="btn-primary inline-flex items-center gap-2" data-testid="track-login-btn"><LogIn className="w-4 h-4" /> Log in to view contacts</Link>
          </div>
        ) : null}

        <div className="flex items-center justify-center gap-2 mt-4 text-[11px] text-[#A09E96]" data-testid="track-refresh-note">
          <RefreshCw className="w-3 h-3" /> Live — auto-refreshes every 15s{updatedAt ? ` · updated ${updatedAt.toLocaleTimeString()}` : ""}
        </div>
      </div>
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="text-[11px] uppercase text-[#68665E]">{label}</div>
      <div className={`font-medium ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function formatTime(t) {
  if (!t) return "";
  try {
    const d = new Date(t.length === 10 ? `${t}T00:00:00` : t);
    if (isNaN(d.getTime())) return t;
    return d.toLocaleString();
  } catch { return t; }
}
